/**
 * Pure markdown -> Lexical converter (no Payload imports) used by article seed/clean
 * scripts, plus helpers to strip inline markdown from plain-text fields.
 */

export const IS_BOLD = 1
export const IS_ITALIC = 2
export const IS_CODE = 16

type AnyNode = Record<string, any>

const textNode = (text: string, format = 0): AnyNode => ({
  type: 'text',
  text,
  format,
  mode: 'normal',
  style: '',
  detail: 0,
  version: 1,
})

const linkNode = (url: string, children: AnyNode[]): AnyNode => ({
  type: 'link',
  direction: 'ltr',
  format: '',
  indent: 0,
  version: 2,
  fields: { linkType: 'custom', newTab: /^https?:/.test(url), url },
  children,
})

/** Index of the closing `]` matching the `[` at `start`, or -1. */
function findBracketEnd(s: string, start: number): number {
  let depth = 0
  for (let i = start; i < s.length; i++) {
    if (s[i] === '\\') i++
    else if (s[i] === '[') depth++
    else if (s[i] === ']' && --depth === 0) return i
  }
  return -1
}

/** Index of the closing `)` for a link url starting after `(` at `start`, or -1. */
function findParenEnd(s: string, start: number): number {
  let depth = 0
  for (let i = start; i < s.length; i++) {
    if (s[i] === '(') depth++
    else if (s[i] === ')' && --depth === 0) return i
  }
  return -1
}

/**
 * Index of the closing delimiter `delim` starting at/after `from`.
 * Skips escaped chars, code spans and (for `*`) longer runs that belong to nested markers.
 */
function findClose(s: string, from: number, delim: string): number {
  for (let i = from; i < s.length; i++) {
    const ch = s[i]
    if (ch === '\\') {
      i++
      continue
    }
    if (ch === '`') {
      const end = s.indexOf('`', i + 1)
      if (end > -1) i = end
      continue
    }
    if (ch === '[') {
      const end = findBracketEnd(s, i)
      if (end > -1 && s[end + 1] === '(') {
        const pe = findParenEnd(s, end + 1)
        if (pe > -1) {
          i = pe
          continue
        }
      }
    }
    if (s.startsWith(delim, i) && i > from) {
      // For a single `*`, a `**` is a nested bold opener/closer, not our closer.
      if (delim === '*' && s[i + 1] === '*') {
        // Closing of "***x***" style: treat `**` followed by non-`*` as bold close, skip pair.
        i++
        continue
      }
      return i
    }
  }
  return -1
}

function merge(nodes: AnyNode[]): AnyNode[] {
  const out: AnyNode[] = []
  for (const n of nodes) {
    const prev = out[out.length - 1]
    if (n.type === 'text' && prev?.type === 'text' && prev.format === n.format) {
      prev.text += n.text
    } else out.push(n)
  }
  return out
}

/** Inline markdown -> Lexical children. Handles nesting: bold, italic, ***both***, code, links. */
export function parseInline(input: string, format = 0): AnyNode[] {
  const nodes: AnyNode[] = []
  let buf = ''
  const flush = () => {
    if (buf) nodes.push(textNode(buf, format))
    buf = ''
  }
  const s = input
  let i = 0
  while (i < s.length) {
    const ch = s[i]
    if (ch === '\\' && i + 1 < s.length && /[\\`*_{}[\]()#+\-.!|~>]/.test(s[i + 1])) {
      buf += s[i + 1]
      i += 2
      continue
    }
    if (ch === '`') {
      const end = s.indexOf('`', i + 1)
      if (end > i + 1) {
        flush()
        nodes.push(textNode(s.slice(i + 1, end), format | IS_CODE))
        i = end + 1
        continue
      }
    }
    if (ch === '*') {
      const run = s.slice(i).match(/^\*+/)![0].length
      const tryDelims: [string, number][] =
        run >= 3 ? [['***', IS_BOLD | IS_ITALIC]] : []
      if (run >= 2) tryDelims.push(['**', IS_BOLD])
      tryDelims.push(['*', IS_ITALIC])
      let done = false
      for (const [delim, fmt] of tryDelims) {
        if (run < delim.length) continue
        const start = i + delim.length
        if (/\s/.test(s[start] ?? ' ')) continue
        const close = findClose(s, start, delim)
        if (close > start && !/\s/.test(s[close - 1])) {
          flush()
          nodes.push(...parseInline(s.slice(start, close), format | fmt))
          i = close + delim.length
          done = true
          break
        }
      }
      if (done) continue
    }
    if (ch === '[') {
      const end = findBracketEnd(s, i)
      if (end > -1 && s[end + 1] === '(') {
        const pe = findParenEnd(s, end + 1)
        if (pe > -1) {
          flush()
          const url = s.slice(end + 2, pe).trim()
          nodes.push(linkNode(url, parseInline(s.slice(i + 1, end), format)))
          i = pe + 1
          continue
        }
      }
    }
    buf += ch
    i++
  }
  flush()
  return merge(nodes.length ? nodes : [textNode('', format)])
}

const stripAnchor = (t: string) => t.replace(/\s*\{#[^}]+\}\s*$/, '')

/**
 * Remove inline markdown from a plain-text value (meta title/description, excerpts).
 * Keeps link labels, drops urls, emphasis markers, code ticks and `{#anchor}` suffixes.
 */
export function stripInlineMarkdown(text: string): string {
  if (typeof text !== 'string' || !text) return text
  const plain = (nodes: AnyNode[]): string =>
    nodes.map((n) => (n.type === 'link' ? plain(n.children) : n.text)).join('')
  return plain(parseInline(stripAnchor(text)))
    .replace(/\s{2,}/g, ' ')
    .trim()
}

const base = { direction: 'ltr', format: '', indent: 0, version: 1 }

const isTableRow = (l: string) => /^\|.*\|$/.test(l.trim())
const isTableSep = (l: string) => /^\|?\s*:?-{2,}:?\s*(\|\s*:?-{2,}:?\s*)*\|?$/.test(l.trim()) && l.includes('-')

function splitRow(line: string): string[] {
  let t = line.trim()
  if (t.startsWith('|')) t = t.slice(1)
  if (t.endsWith('|') && !t.endsWith('\\|')) t = t.slice(0, -1)
  return t.split(/(?<!\\)\|/).map((c) => c.trim().replace(/\\\|/g, '|'))
}

function tableNode(rows: string[][]): AnyNode {
  const width = Math.max(...rows.map((r) => r.length))
  return {
    type: 'table',
    ...base,
    children: rows.map((cells, r) => ({
      type: 'tablerow',
      ...base,
      children: Array.from({ length: width }, (_, c) => ({
        type: 'tablecell',
        ...base,
        headerState: r === 0 ? 1 : 0,
        colSpan: 1,
        rowSpan: 1,
        backgroundColor: null,
        children: [
          { type: 'paragraph', ...base, textFormat: 0, children: parseInline(cells[c] ?? '') },
        ],
      })),
    })),
  }
}

/** Markdown body -> Lexical block children. `##`/`###` -> h2, `####` -> h3 (article convention). */
export function markdownToLexical(markdown: string): AnyNode[] {
  const lines = markdown.split(/\r?\n/)
  const children: AnyNode[] = []

  // Standard markdown (## = h2, ### = h3) when there is no #### and >= 2 `##` headings;
  // otherwise the Wave 1/2 convention (### = h2, #### = h3).
  const inFence = (() => {
    let open = false
    return lines.map((l) => {
      if (l.trim().startsWith('```')) {
        open = !open
        return true
      }
      return open
    })
  })()
  const body = lines.filter((_, idx) => !inFence[idx])
  const standardHeadings =
    !body.some((l) => /^####\s/.test(l.trim())) &&
    body.filter((l) => /^##\s/.test(l.trim())).length >= 2

  let inList: 'bullet' | 'number' | null = null
  let listItems: AnyNode[] = []
  let quote: string[] = []

  const flushList = () => {
    if (!inList) return
    children.push({
      type: 'list',
      listType: inList,
      tag: inList === 'bullet' ? 'ul' : 'ol',
      start: 1,
      ...base,
      children: listItems,
    })
    inList = null
    listItems = []
  }
  const flushQuote = () => {
    if (!quote.length) return
    children.push({ type: 'quote', ...base, children: parseInline(quote.join(' ')) })
    quote = []
  }

  for (let i = 0; i < lines.length; i++) {
    const trimmed = lines[i].trim()

    if (!trimmed) {
      flushList()
      flushQuote()
      continue
    }

    // Fenced code block: keep inner lines (with line breaks) as a quote block
    if (trimmed.startsWith('```')) {
      flushList()
      flushQuote()
      const inner: string[] = []
      i++
      while (i < lines.length && !lines[i].trim().startsWith('```')) inner.push(lines[i++])
      const nodes: AnyNode[] = []
      inner
        .filter((l) => l.trim())
        .forEach((l, idx) => {
          if (idx > 0) nodes.push({ type: 'linebreak', version: 1 })
          nodes.push(...parseInline(l.trim()))
        })
      if (nodes.length) children.push({ type: 'quote', ...base, children: nodes })
      continue
    }

    // Standalone HTML anchors and horizontal rules are not content
    if (/^<a\s+id=["'][^"']+["']\s*>\s*<\/a>$/i.test(trimmed) || /^---+$/.test(trimmed)) {
      flushList()
      flushQuote()
      continue
    }

    if (standardHeadings) {
      const std = trimmed.match(/^(#{2,3})\s+(.*)$/)
      if (std) {
        flushList()
        flushQuote()
        children.push({
          type: 'heading',
          tag: std[1].length === 2 ? 'h2' : 'h3',
          ...base,
          children: parseInline(stripAnchor(std[2])),
        })
        continue
      }
    }

    // Title (# / ##) comes from page.title
    if (/^#{1,2}\s/.test(trimmed)) {
      flushList()
      flushQuote()
      continue
    }

    const heading = trimmed.match(/^(#{3,4})\s+(.*)$/)
    if (heading) {
      flushList()
      flushQuote()
      children.push({
        type: 'heading',
        tag: heading[1].length === 3 ? 'h2' : 'h3',
        ...base,
        children: parseInline(stripAnchor(heading[2])),
      })
      continue
    }

    if (trimmed.startsWith('>')) {
      flushList()
      quote.push(trimmed.replace(/^>\s?/, ''))
      continue
    }
    flushQuote()

    // GFM table: header row + separator row
    if (isTableRow(trimmed) && i + 1 < lines.length && isTableSep(lines[i + 1])) {
      flushList()
      const rows = [splitRow(trimmed)]
      i += 2
      while (i < lines.length && isTableRow(lines[i])) rows.push(splitRow(lines[i++]))
      i--
      children.push(tableNode(rows))
      continue
    }

    const bullet = trimmed.match(/^[-*]\s+(.*)$/)
    const numbered = trimmed.match(/^\d+\.\s+(.*)$/)
    if (bullet || numbered) {
      const kind = bullet ? 'bullet' : 'number'
      if (inList !== kind) {
        flushList()
        inList = kind
      }
      listItems.push({
        type: 'listitem',
        value: listItems.length + 1,
        ...base,
        children: parseInline((bullet || numbered)![1]),
      })
      continue
    }

    flushList()
    children.push({ type: 'paragraph', ...base, textFormat: 0, children: parseInline(trimmed) })
  }

  flushList()
  flushQuote()
  return children
}

/**
 * Re-tokenize a Lexical text node whose text contains literal markdown emphasis.
 * Returns the replacement nodes (the original node when nothing to fix).
 */
export function reparseTextNode(node: AnyNode): AnyNode[] {
  if (node.type !== 'text' || typeof node.text !== 'string' || !/\*/.test(node.text)) return [node]
  const out: AnyNode[] = parseInline(node.text, node.format ?? 0).map((n) => ({
    ...n,
    style: node.style ?? '',
    mode: node.mode ?? 'normal',
  }))
  const unchanged = out.length === 1 && out[0].type === 'text' && out[0].text === node.text
  return unchanged ? [node] : out
}

/** Walk a Lexical tree and re-tokenize every text node. Returns number of fixed nodes. */
export function fixLexicalTree(node: AnyNode): number {
  if (!Array.isArray(node.children)) return 0
  let fixed = 0
  const next: AnyNode[] = []
  for (const child of node.children) {
    if (child.type === 'text') {
      const rep = reparseTextNode(child)
      if (rep.length !== 1 || rep[0] !== child) fixed++
      next.push(...rep)
    } else {
      fixed += fixLexicalTree(child)
      next.push(child)
    }
  }
  node.children = next
  return fixed
}
